import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, UserPlus, Trash2, Edit, Key, ChefHat, 
  UtensilsCrossed, Wine, ShieldCheck, Save, X,
  CheckCircle, XCircle, Eye, EyeOff, ChevronLeft
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import API from '../utils/api';

// Labels per i ruoli del team
const TEAM_ROLE_LABELS = {
  executive_chef: "Executive Chef",
  sous_chef: "Sous Chef",
  chef_de_partie: "Chef de Partie",
  commis: "Commis",
  pasticcere: "Pasticcere",
  pizzaiolo: "Pizzaiolo",
  maitre: "Maître",
  sommelier: "Sommelier",
  cameriere: "Cameriere",
  barman: "Barman",
  hostess: "Hostess",
  magazziniere: "Magazziniere",
  cassiere: "Cassiere",
  custom: "Personalizzato"
};

// Icone per categoria
const ROLE_ICONS = {
  executive_chef: ChefHat,
  sous_chef: ChefHat,
  chef_de_partie: ChefHat,
  commis: UtensilsCrossed,
  pasticcere: ChefHat,
  pizzaiolo: ChefHat,
  maitre: Users,
  sommelier: Wine,
  cameriere: Users,
  barman: Wine,
  hostess: Users,
  magazziniere: ShieldCheck,
  cassiere: ShieldCheck,
  custom: Users
};

// Sezioni disponibili
const AVAILABLE_SECTIONS = {
  ordini: { label: "Ordini", description: "Gestione ordini clienti" },
  ddt: { label: "DDT/Bolle", description: "Documenti di trasporto" },
  haccp: { label: "HACCP", description: "Controlli igienico-sanitari" },
  fatture: { label: "Fatture", description: "Fatture elettroniche" },
  fb_cost: { label: "F&B Cost", description: "Analisi costi" },
  menu: { label: "Menu", description: "Gestione menu" },
  spese: { label: "Costi/Ricavi", description: "Registro costi e ricavi" },
  magazzino: { label: "Magazzino", description: "Inventario e scorte" },
  lista_vini: { label: "Lista Vini", description: "Carta dei vini" }
};

// Sezioni default per ruolo
const ROLE_DEFAULT_SECTIONS = {
  executive_chef: ["ordini", "menu", "magazzino", "fb_cost", "haccp"],
  sous_chef: ["ordini", "menu", "magazzino", "haccp"],
  chef_de_partie: ["ordini", "menu", "magazzino"],
  commis: ["ordini", "magazzino"],
  pasticcere: ["ordini", "menu", "magazzino"],
  pizzaiolo: ["ordini", "menu", "magazzino"],
  maitre: ["ordini", "lista_vini", "menu"],
  sommelier: ["ordini", "lista_vini", "magazzino"],
  cameriere: ["ordini"],
  barman: ["ordini", "magazzino"],
  hostess: ["ordini"],
  magazziniere: ["magazzino", "ddt", "fatture"],
  cassiere: ["ordini", "spese", "fatture"],
  custom: []
};

const TeamManagementPage = ({ tenantId, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [maxUsers, setMaxUsers] = useState(0);
  const [canAddMore, setCanAddMore] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Detect mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
  
  const [newMember, setNewMember] = useState({
    username: '',
    password: '',
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    team_role: 'cameriere',
    custom_role_name: '',
    enabled_sections: ['ordini']
  });
  
  const [newPassword, setNewPassword] = useState('');

  const [plan, setPlan] = useState('');
  const [planMaxUsers, setPlanMaxUsers] = useState(0);
  const [maxUsersOverride, setMaxUsersOverride] = useState(null);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('tenant_token') || localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'X-Tenant-DB': localStorage.getItem('tenant_db') || ''
    };
  }, []);

  const loadTeamMembers = useCallback(async () => {
    if (!tenantId) {
      console.warn('loadTeamMembers: tenantId non definito');
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(
        `${API}/saas/tenants/${tenantId}/team`,
        { headers: getHeaders() }
      );
      setTeamMembers(response.data.team_members || []);
      setTotalUsers(response.data.total_users || 0);
      setMaxUsers(response.data.max_users || 0);
      setCanAddMore(response.data.can_add_more);
      setPlan(response.data.plan || 'free');
      setPlanMaxUsers(response.data.plan_max_users || 0);
      setMaxUsersOverride(response.data.max_users_override);
    } catch (error) {
      console.error('Errore caricamento team:', error);
      toast.error('Errore nel caricamento del team');
    } finally {
      setLoading(false);
    }
  }, [tenantId, getHeaders]);

  useEffect(() => {
    loadTeamMembers();
  }, [loadTeamMembers]);

  const handleRoleChange = (role) => {
    setNewMember(prev => ({
      ...prev,
      team_role: role,
      enabled_sections: ROLE_DEFAULT_SECTIONS[role] || []
    }));
  };

  const toggleSection = (section) => {
    setNewMember(prev => ({
      ...prev,
      enabled_sections: prev.enabled_sections.includes(section)
        ? prev.enabled_sections.filter(s => s !== section)
        : [...prev.enabled_sections, section]
    }));
  };

  const handleCreateMember = async () => {
    if (!tenantId) {
      toast.error('Errore: ID tenant non disponibile');
      return;
    }
    
    if (!newMember.username || !newMember.password || !newMember.nome || !newMember.cognome) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/saas/tenants/${tenantId}/team`,
        newMember,
        { headers: getHeaders() }
      );
      toast.success('Membro del team creato!');
      setShowAddMember(false);
      setNewMember({
        username: '',
        password: '',
        nome: '',
        cognome: '',
        email: '',
        telefono: '',
        team_role: 'cameriere',
        custom_role_name: '',
        enabled_sections: ['ordini']
      });
      loadTeamMembers();
    } catch (error) {
      console.error('Errore creazione membro:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMember = async () => {
    if (!editingMember) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/saas/tenants/${tenantId}/team/${editingMember.id}`,
        {
          nome: editingMember.nome,
          cognome: editingMember.cognome,
          email: editingMember.email,
          telefono: editingMember.telefono,
          team_role: editingMember.team_role,
          custom_role_name: editingMember.custom_role_name,
          enabled_sections: editingMember.enabled_sections,
          is_active: editingMember.is_active
        },
        { headers: getHeaders() }
      );
      toast.success('Membro aggiornato!');
      setEditingMember(null);
      loadTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!confirm('Sei sicuro di voler eliminare questo membro del team?')) return;
    
    try {
      await axios.delete(
        `${API}/saas/tenants/${tenantId}/team/${memberId}`,
        { headers: getHeaders() }
      );
      toast.success('Membro eliminato');
      loadTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/saas/tenants/${tenantId}/team/${showResetPassword.id}/reset-password`,
        { new_password: newPassword },
        { headers: getHeaders() }
      );
      toast.success('Password aggiornata!');
      setShowResetPassword(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel reset password');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className={onBack ? "mobile-content-wrapper min-h-screen bg-gradient-to-b from-violet-950 via-slate-950 to-slate-950" : "space-y-6"}>
      {/* Header - Mobile vs Desktop */}
      {onBack ? (
        // Mobile Header con safe area
        <div className="sticky top-0 z-50 bg-black/30 backdrop-blur-md pb-3 px-4 flex items-center gap-3" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}>
          <button 
            onClick={onBack}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Users className="h-5 w-5" />
              Gestione Team
            </h1>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-violet-200 text-xs">
                {totalUsers} utenti totali {maxUsers > 0 ? `/ ${maxUsers} max` : '(illimitati)'}
              </p>
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                plan === 'enterprise' ? 'bg-purple-400/30 text-purple-200' :
                plan === 'professional' ? 'bg-blue-400/30 text-blue-200' :
                plan === 'starter' ? 'bg-green-400/30 text-green-200' :
                'bg-gray-400/30 text-gray-200'
              }`}>
                Piano {plan?.charAt(0).toUpperCase() + plan?.slice(1)} 
                {planMaxUsers !== 'Illimitato' && ` (${planMaxUsers} utenti)`}
              </span>
            </div>
          </div>
          {canAddMore && (
            <button
              onClick={() => setShowAddMember(true)}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-violet-500 text-white"
            >
              <UserPlus className="h-5 w-5" />
            </button>
          )}
        </div>
      ) : (
        // Desktop Header
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestione Team</h1>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-gray-500">
                {totalUsers} utenti totali {maxUsers > 0 ? `/ ${maxUsers} max` : '(illimitati)'}
              </p>
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                plan === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                plan === 'professional' ? 'bg-blue-100 text-blue-700' :
                plan === 'starter' ? 'bg-green-100 text-green-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                Piano {plan?.charAt(0).toUpperCase() + plan?.slice(1)} 
                {planMaxUsers !== 'Illimitato' && ` (${planMaxUsers} utenti)`}
                {planMaxUsers === 'Illimitato' && ' (∞)'}
              </span>
              {maxUsersOverride && maxUsersOverride > 0 && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-700">
                  Override: {maxUsersOverride}
                </span>
              )}
            </div>
          </div>
          <Button 
            onClick={() => setShowAddMember(true)} 
            disabled={!canAddMore}
            className="flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Aggiungi Membro
          </Button>
        </div>
      )}

      {/* Contenuto - con padding per mobile */}
      <div className={onBack ? "px-4 pb-20 space-y-4" : ""}>
        {!canAddMore && maxUsers > 0 && (
          <div className={`border rounded-lg p-4 ${onBack ? 'bg-amber-500/20 border-amber-500/30 text-amber-100' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
            <p className="font-medium">Limite utenti raggiunto</p>
            <p className="text-sm">Hai raggiunto il limite di {maxUsers} utenti. Contatta l'amministratore per aumentare il limite.</p>
          </div>
        )}

        {/* Team Members Grid */}
        <div className={`grid gap-4 ${onBack ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
        {teamMembers.map((member) => {
          const RoleIcon = ROLE_ICONS[member.team_role] || Users;
          return (
            <Card key={member.id} className={`${!member.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      member.is_active ? 'bg-cyan-100 text-cyan-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      <RoleIcon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {member.nome} {member.cognome}
                      </p>
                      <p className="text-sm text-gray-500">@{member.username}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setEditingMember({...member})}
                      className="p-1.5 hover:bg-gray-100 rounded"
                      title="Modifica"
                    >
                      <Edit className="h-4 w-4 text-gray-500" />
                    </button>
                    <button
                      onClick={() => setShowResetPassword(member)}
                      className="p-1.5 hover:bg-gray-100 rounded"
                      title="Reset Password"
                    >
                      <Key className="h-4 w-4 text-gray-500" />
                    </button>
                    <button
                      onClick={() => handleDeleteMember(member.id)}
                      className="p-1.5 hover:bg-red-50 rounded"
                      title="Elimina"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                </div>
                
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs font-medium text-gray-500 mb-1">Ruolo</p>
                  <p className="text-sm text-gray-700">
                    {member.team_role === 'custom' 
                      ? member.custom_role_name 
                      : TEAM_ROLE_LABELS[member.team_role] || member.team_role}
                  </p>
                </div>
                
                <div className="mt-2">
                  <p className="text-xs font-medium text-gray-500 mb-1">Sezioni abilitate</p>
                  <div className="flex flex-wrap gap-1">
                    {(member.enabled_sections || []).map(section => (
                      <span 
                        key={section} 
                        className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                      >
                        {AVAILABLE_SECTIONS[section]?.label || section}
                      </span>
                    ))}
                    {(!member.enabled_sections || member.enabled_sections.length === 0) && (
                      <span className="text-xs text-gray-400">Nessuna</span>
                    )}
                  </div>
                </div>

                {!member.is_active && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-red-500">
                    <XCircle className="h-3 w-3" />
                    Disattivato
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        
        {teamMembers.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Nessun membro del team</p>
            <p className="text-sm">Clicca "Aggiungi Membro" per iniziare</p>
          </div>
        )}
        </div>
      </div>

      {/* Modal Aggiungi Membro */}
      {showAddMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Nuovo Membro del Team
              </CardTitle>
              <CardDescription>Aggiungi un nuovo membro al tuo team di lavoro</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Dati personali */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome *</Label>
                  <Input
                    value={newMember.nome}
                    onChange={(e) => setNewMember({...newMember, nome: e.target.value})}
                    placeholder="Mario"
                  />
                </div>
                <div>
                  <Label>Cognome *</Label>
                  <Input
                    value={newMember.cognome}
                    onChange={(e) => setNewMember({...newMember, cognome: e.target.value})}
                    placeholder="Rossi"
                  />
                </div>
              </div>

              {/* Credenziali */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Username *</Label>
                  <Input
                    value={newMember.username}
                    onChange={(e) => setNewMember({...newMember, username: e.target.value})}
                    placeholder="mrossi"
                  />
                </div>
                <div>
                  <Label>Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={newMember.password}
                      onChange={(e) => setNewMember({...newMember, password: e.target.value})}
                      placeholder="Min 6 caratteri"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Contatti */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                    placeholder="mario@email.com"
                  />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input
                    value={newMember.telefono}
                    onChange={(e) => setNewMember({...newMember, telefono: e.target.value})}
                    placeholder="333 1234567"
                  />
                </div>
              </div>

              {/* Ruolo */}
              <div>
                <Label>Ruolo nel Team</Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                  {Object.entries(TEAM_ROLE_LABELS).map(([value, label]) => {
                    const Icon = ROLE_ICONS[value] || Users;
                    return (
                      <button
                        key={value}
                        onClick={() => handleRoleChange(value)}
                        className={`p-2 rounded-lg border text-sm flex flex-col items-center gap-1 transition-all ${
                          newMember.team_role === value
                            ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="text-xs">{label}</span>
                      </button>
                    );
                  })}
                </div>
                {newMember.team_role === 'custom' && (
                  <Input
                    className="mt-2"
                    value={newMember.custom_role_name}
                    onChange={(e) => setNewMember({...newMember, custom_role_name: e.target.value})}
                    placeholder="Nome ruolo personalizzato"
                  />
                )}
              </div>

              {/* Sezioni abilitate */}
              <div>
                <Label>Sezioni Abilitate</Label>
                <p className="text-xs text-gray-500 mb-2">
                  Seleziona le aree a cui questo membro avrà accesso
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(AVAILABLE_SECTIONS).map(([key, section]) => (
                    <button
                      key={key}
                      onClick={() => toggleSection(key)}
                      className={`p-2 rounded-lg border text-left transition-all ${
                        newMember.enabled_sections.includes(key)
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {newMember.enabled_sections.includes(key) 
                          ? <CheckCircle className="h-4 w-4 text-green-500" />
                          : <div className="h-4 w-4 border rounded" />
                        }
                        <span className="text-sm font-medium">{section.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowAddMember(false)}>
                  Annulla
                </Button>
                <Button onClick={handleCreateMember} disabled={saving}>
                  {saving ? 'Creazione...' : 'Crea Membro'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Modifica Membro */}
      {editingMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Modifica {editingMember.nome} {editingMember.cognome}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Stato attivo */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Stato Account</p>
                  <p className="text-sm text-gray-500">
                    {editingMember.is_active ? 'Attivo - può accedere' : 'Disattivato - accesso bloccato'}
                  </p>
                </div>
                <Switch
                  checked={editingMember.is_active}
                  onCheckedChange={(checked) => setEditingMember({...editingMember, is_active: checked})}
                />
              </div>

              {/* Dati personali */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome</Label>
                  <Input
                    value={editingMember.nome}
                    onChange={(e) => setEditingMember({...editingMember, nome: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Cognome</Label>
                  <Input
                    value={editingMember.cognome}
                    onChange={(e) => setEditingMember({...editingMember, cognome: e.target.value})}
                  />
                </div>
              </div>

              {/* Contatti */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editingMember.email || ''}
                    onChange={(e) => setEditingMember({...editingMember, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input
                    value={editingMember.telefono || ''}
                    onChange={(e) => setEditingMember({...editingMember, telefono: e.target.value})}
                  />
                </div>
              </div>

              {/* Ruolo */}
              <div>
                <Label>Ruolo</Label>
                <select
                  value={editingMember.team_role}
                  onChange={(e) => setEditingMember({
                    ...editingMember, 
                    team_role: e.target.value,
                    enabled_sections: ROLE_DEFAULT_SECTIONS[e.target.value] || editingMember.enabled_sections
                  })}
                  className="w-full mt-1 p-2 border rounded-lg"
                >
                  {Object.entries(TEAM_ROLE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                {editingMember.team_role === 'custom' && (
                  <Input
                    className="mt-2"
                    value={editingMember.custom_role_name || ''}
                    onChange={(e) => setEditingMember({...editingMember, custom_role_name: e.target.value})}
                    placeholder="Nome ruolo personalizzato"
                  />
                )}
              </div>

              {/* Sezioni */}
              <div>
                <Label>Sezioni Abilitate</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                  {Object.entries(AVAILABLE_SECTIONS).map(([key, section]) => (
                    <button
                      key={key}
                      onClick={() => {
                        const sections = editingMember.enabled_sections || [];
                        setEditingMember({
                          ...editingMember,
                          enabled_sections: sections.includes(key)
                            ? sections.filter(s => s !== key)
                            : [...sections, key]
                        });
                      }}
                      className={`p-2 rounded-lg border text-left transition-all ${
                        (editingMember.enabled_sections || []).includes(key)
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {(editingMember.enabled_sections || []).includes(key)
                          ? <CheckCircle className="h-4 w-4 text-green-500" />
                          : <div className="h-4 w-4 border rounded" />
                        }
                        <span className="text-sm font-medium">{section.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditingMember(null)}>
                  Annulla
                </Button>
                <Button onClick={handleUpdateMember} disabled={saving}>
                  {saving ? 'Salvataggio...' : 'Salva Modifiche'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Reset Password */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Reset Password
              </CardTitle>
              <CardDescription>
                Imposta una nuova password per {showResetPassword.nome} {showResetPassword.cognome}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Nuova Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min 6 caratteri"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowResetPassword(null); setNewPassword(''); }}>
                  Annulla
                </Button>
                <Button onClick={handleResetPassword} disabled={saving}>
                  {saving ? 'Aggiornamento...' : 'Aggiorna Password'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TeamManagementPage;
